import { createContext, useContext, ReactNode, useState, useEffect } from "react";

export interface Company {
  companyID: string;
  name: string;
}

interface CompanyContextType {
  companies: Company[];
  activeCompany: Company | null;
  setActiveCompany: (companyId: string) => void;
  isLoading: boolean;
  isChangingCompany: boolean;
  initializeCompanies: (companies: Company[]) => void;
}

const CompanyContext = createContext<CompanyContextType>({
  companies: [],
  activeCompany: null,
  setActiveCompany: () => { },
  isLoading: true,
  isChangingCompany: false,
  initializeCompanies: () => { },
});

export const useCompanyContext = () => useContext(CompanyContext);

const ACTIVE_COMPANY_STORAGE_KEY = "activeCompanyId";

export const CompanyProvider = ({ children }: { children: ReactNode }) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [activeCompany, setActiveCompanyState] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isChangingCompany, setIsChangingCompany] = useState(false);

  // Fallback: If loading for too long without companies, stop loading
  // Only trigger this if we've been loading for a while AND no initializeCompanies has been called
  useEffect(() => {
    // Only set timeout if we're still loading and have no companies
    // This gives initializeCompanies time to be called
    if (!isLoading || companies.length > 0) {
      return; // Don't set timeout if already loaded or companies exist
    }

    const timeout = setTimeout(() => {
      // Double-check we're still in loading state with no companies
      if (isLoading && companies.length === 0) {
        console.warn('🏢 CompanyContext: Loading timeout - no companies initialized after 5 seconds, stopping loading');
        setIsLoading(false);
      }
    }, 5000); // 5 second timeout

    return () => clearTimeout(timeout);
  }, [isLoading, companies.length]);

  // Initialize companies from authentication response
  const initializeCompanies = (companiesList: Company[]) => {
    console.log('🏢 CompanyContext: initializeCompanies called with:', companiesList);
    console.log('🏢 CompanyContext: Current state before update - companies:', companies.length, 'activeCompany:', activeCompany, 'isLoading:', isLoading);

    if (!companiesList || companiesList.length === 0) {
      console.warn('🏢 CompanyContext: No companies provided, setting loading to false');
      setIsLoading(false);
      return;
    }

    console.log('🏢 CompanyContext: Setting companies state with:', companiesList);
    setCompanies(companiesList);

    // Always use first company as default
    const defaultCompany = companiesList[0];
    console.log('🏢 CompanyContext: Setting active company state to:', defaultCompany);
    setActiveCompanyState(defaultCompany);

    // Store in localStorage for persistence across refreshes
    localStorage.setItem(ACTIVE_COMPANY_STORAGE_KEY, defaultCompany.companyID);

    setIsLoading(false);
    console.log('🏢 CompanyContext: State updates queued - companies should be:', companiesList.length, 'activeCompany should be:', defaultCompany.name);
    console.log('🏢 CompanyContext: Initialization complete - React will re-render with new state');
  };

  // Set active company
  const setActiveCompany = (companyId: string) => {
    const company = companies.find((c) => c.companyID === companyId);
    if (company && company.companyID !== activeCompany?.companyID) {
      setIsChangingCompany(true);
      setActiveCompanyState(company);
      localStorage.setItem(ACTIVE_COMPANY_STORAGE_KEY, companyId);

      // Trigger a custom event so components can react to company change
      window.dispatchEvent(new CustomEvent('companyChanged', { detail: { companyId } }));

      // Reset changing state after a delay (queries will handle the actual loading)
      setTimeout(() => {
        setIsChangingCompany(false);
      }, 500);
    }
  };

  // Listen for company change events to track loading state
  useEffect(() => {
    const handleCompanyChange = () => {
      setIsChangingCompany(true);
      // Reset after queries have time to refetch
      const timeout = setTimeout(() => {
        setIsChangingCompany(false);
      }, 2000);
      return () => clearTimeout(timeout);
    };

    window.addEventListener('companyChanged', handleCompanyChange);
    return () => window.removeEventListener('companyChanged', handleCompanyChange);
  }, []);

  // Load active company from localStorage on mount (if companies are already initialized)
  useEffect(() => {
    console.log('🏢 CompanyContext: useEffect - companies:', companies.length, 'activeCompany:', activeCompany, 'isLoading:', isLoading);

    if (companies.length > 0 && !activeCompany) {
      const storedCompanyId = localStorage.getItem(ACTIVE_COMPANY_STORAGE_KEY);
      const companyToUse = storedCompanyId
        ? companies.find((c) => c.companyID === storedCompanyId) || companies[0]
        : companies[0];

      console.log('🏢 CompanyContext: Setting active company from localStorage:', companyToUse);
      setActiveCompanyState(companyToUse);
      localStorage.setItem(ACTIVE_COMPANY_STORAGE_KEY, companyToUse.companyID);
      setIsLoading(false);
    }
    // Removed the else clause that was prematurely stopping loading
    // The initializeCompanies function handles setting isLoading to false
  }, [companies, activeCompany]);

  return (
    <CompanyContext.Provider
      value={{
        companies,
        activeCompany,
        setActiveCompany,
        isLoading,
        isChangingCompany,
        initializeCompanies,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};


